import { Checkbox, FormControlLabel, Stack, TextField, Tooltip, Typography } from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import type { Dispatch, SetStateAction } from "react";
import type { BulkMessageDelaySettings as DelaySettings } from "utils/contacts/bulkMessage/new";

type BulkMessageDialogDelaySettingsProps = {
  delaySettings: DelaySettings;
  setDelaySettings: Dispatch<SetStateAction<DelaySettings>>;
};

export function BulkMessageDialogDelaySettings({
  delaySettings,
  setDelaySettings
}: BulkMessageDialogDelaySettingsProps): JSX.Element {
  const { t: translate } = useTranslation();

  const handleChangeMin = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = Math.max(0, Number(e.target.value));
    setDelaySettings((prev) => ({ ...prev, delayMinSeconds: value }));
  };

  const handleChangeMax = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = Math.max(0, Number(e.target.value));
    setDelaySettings((prev) => ({ ...prev, delayMaxSeconds: value }));
  };

  const handleBlurMinMax = (): void => {
    setDelaySettings((prev) => {
      const min = prev.delayMinSeconds;
      const max = prev.delayMaxSeconds;
      if (max < min) return { ...prev, delayMaxSeconds: min };
      return prev;
    });
  };

  const handleToggleCooldown = (): void => {
    setDelaySettings((prev) => ({
      ...prev,
      cooldownEnabled: !prev.cooldownEnabled
    }));
  };

  const handleChangeCooldownCount = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setDelaySettings((prev) => ({
      ...prev,
      cooldownAfterCount: Math.max(1, Number(e.target.value))
    }));
  };

  const handleChangeCooldownDuration = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setDelaySettings((prev) => ({
      ...prev,
      cooldownDurationSeconds: Math.max(60, Number(e.target.value) * 60)
    }));
  };

  return (
    <Stack gap={1.5}>
      <Stack direction="row" alignItems="center" gap={0.75}>
        <Typography variant="subtitle2">
          {translate("contactsPage.bulkMessage.delaySettings.title")}
        </Typography>
        {/*
          These values are saved to the ACCOUNT, not to this send — they were per-browser
          `localStorage` before and are `userSettings.bulkMessageDelaySettings` now. The
          hint says so, because a control inside a send wizard otherwise reads as
          "settings for this batch", and a realtor slowing things down for one risky list
          would not expect it to stick.
        */}
        <Tooltip title={translate("settingsPage.bulkMessaging.description") as string}>
          <InfoOutlined sx={{ fontSize: 15, color: "text.disabled" }} />
        </Tooltip>
      </Stack>
      <Stack direction="row" gap={2} alignItems="center">
        <TextField
          size="small"
          type="number"
          label={translate("contactsPage.bulkMessage.delaySettings.minDelay")}
          value={delaySettings.delayMinSeconds}
          onChange={handleChangeMin}
          onBlur={handleBlurMinMax}
          inputProps={{ min: 0 }}
          sx={{ width: 130 }}
        />
        <Typography variant="body2">-</Typography>
        <TextField
          size="small"
          type="number"
          label={translate("contactsPage.bulkMessage.delaySettings.maxDelay")}
          value={delaySettings.delayMaxSeconds}
          onChange={handleChangeMax}
          onBlur={handleBlurMinMax}
          inputProps={{ min: 0 }}
          sx={{ width: 130 }}
        />
        <Typography variant="body2">
          {translate("contactsPage.bulkMessage.delaySettings.seconds")}
        </Typography>
      </Stack>
      <FormControlLabel
        control={
          <Checkbox checked={delaySettings.cooldownEnabled} onChange={handleToggleCooldown} />
        }
        label={translate("contactsPage.bulkMessage.delaySettings.enableCooldown")}
      />
      {delaySettings.cooldownEnabled && (
        <Stack direction="row" gap={2} alignItems="center">
          <Typography variant="body2">
            {translate("contactsPage.bulkMessage.delaySettings.afterEvery")}
          </Typography>
          <TextField
            size="small"
            type="number"
            value={delaySettings.cooldownAfterCount}
            onChange={handleChangeCooldownCount}
            inputProps={{ min: 1 }}
            sx={{ width: 80 }}
          />
          <Typography variant="body2">
            {translate("contactsPage.bulkMessage.delaySettings.messagesWait")}
          </Typography>
          <TextField
            size="small"
            type="number"
            value={Math.round(delaySettings.cooldownDurationSeconds / 60)}
            onChange={handleChangeCooldownDuration}
            inputProps={{ min: 1 }}
            sx={{ width: 80 }}
          />
          <Typography variant="body2">
            {translate("contactsPage.bulkMessage.delaySettings.minutes")}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
